import type {
  CompletedRefundTransaction,
  FailedRefundTransaction,
  Payment as GraphQlPayment,
  RefundInvoice,
} from '~/api/graphql/generated/types'
import type { Payment } from '~/types'
import { trackEvent, TrackingEvent } from '~/utils/trackingProvider'

function getInvoiceType(refundInvoice: RefundInvoice): string | undefined {
  return refundInvoice.originalInvoice?.type ?? 'INVOICE'
}

export function calculateProcessingDurationMs(
  refundStartTime: number | null,
): number | undefined {
  return refundStartTime ? Date.now() - refundStartTime : undefined
}
export interface TrackUnlinkedRefundStartedParams {
  businessId: string
  refundAmount: number
  refundInvoice: RefundInvoice
  selectedPayment: Payment | GraphQlPayment
  terminalId?: string | null
}

export function trackUnlinkedRefundStarted({
  refundInvoice,
  selectedPayment,
  refundAmount,
  businessId,
  terminalId,
}: TrackUnlinkedRefundStartedParams): void {
  trackEvent(TrackingEvent.ADD_PAYMENT_V2_UNLINKED_REFUND_STARTED, {
    timestamp: Date.now(),
    invoiceId: refundInvoice.originalInvoiceId,
    invoiceType: getInvoiceType(refundInvoice),
    businessId,
    customerId: refundInvoice.client?.id || null,
    paymentId: selectedPayment.id,
    refundAmount,
    terminalId: terminalId || null,
  })
}

export interface TrackUnlinkedRefundProcessingParams {
  businessId: string
  refundAmount: number
  refundInvoice: RefundInvoice
  selectedPaymentId: string
  terminalId?: string | null
  transactionId: string
}

export function trackUnlinkedRefundProcessing({
  refundInvoice,
  selectedPaymentId,
  refundAmount,
  businessId,
  terminalId,
  transactionId,
}: TrackUnlinkedRefundProcessingParams): void {
  trackEvent(TrackingEvent.ADD_PAYMENT_V2_UNLINKED_REFUND_PROCESSING, {
    timestamp: Date.now(),
    invoiceId: refundInvoice.originalInvoiceId,
    invoiceType: getInvoiceType(refundInvoice),
    businessId,
    customerId: refundInvoice.client?.id || null,
    transactionId,
    refundAmount,
    paymentId: selectedPaymentId,
    terminalId: terminalId || null,
  })
}

export interface TrackUnlinkedRefundSuccessParams {
  businessId: string
  refundInvoice: RefundInvoice
  refundStartTime: number | null
  selectedPaymentId?: string | null
  terminalId?: string | null
  transaction: CompletedRefundTransaction
}

export function trackUnlinkedRefundSuccess({
  refundInvoice,
  selectedPaymentId,
  transaction,
  businessId,
  terminalId,
  refundStartTime,
}: TrackUnlinkedRefundSuccessParams): void {
  trackEvent(TrackingEvent.ADD_PAYMENT_V2_UNLINKED_REFUND_SUCCESS, {
    timestamp: Date.now(),
    invoiceId: refundInvoice.originalInvoiceId,
    invoiceType: getInvoiceType(refundInvoice),
    businessId,
    customerId: refundInvoice.client?.id || null,
    transactionId: transaction.id,
    refundAmount: transaction.amount,
    paymentId: selectedPaymentId || null,
    processingDurationMs: calculateProcessingDurationMs(refundStartTime),
    terminalId: terminalId || null,
  })
}

export interface TrackUnlinkedRefundFailureParams {
  businessId: string
  failureReason: string | null
  refundAmount: number
  refundInvoice: RefundInvoice
  refundStartTime: number | null
  selectedPaymentId?: string | null
  terminalId?: string | null
  transaction: FailedRefundTransaction
}

export function trackUnlinkedRefundFailure({
  refundInvoice,
  selectedPaymentId,
  transaction,
  refundAmount,
  businessId,
  terminalId,
  failureReason,
  refundStartTime,
}: TrackUnlinkedRefundFailureParams): void {
  trackEvent(TrackingEvent.ADD_PAYMENT_V2_UNLINKED_REFUND_FAILURE, {
    timestamp: Date.now(),
    invoiceId: refundInvoice.originalInvoiceId,
    invoiceType: getInvoiceType(refundInvoice),
    businessId,
    customerId: refundInvoice.client?.id || null,
    transactionId: transaction.id,
    refundAmount,
    paymentId: selectedPaymentId || null,
    failureReason,
    processingDurationMs: calculateProcessingDurationMs(refundStartTime),
    terminalId: terminalId || null,
  })
}

export interface TrackUnlinkedRefundHardFailureParams {
  businessId: string
  errorMessage?: string | null
  refundAmount: number
  refundInvoice: RefundInvoice
  refundStartTime: number | null
  selectedPaymentId?: string | null
  terminalId?: string | null
  transactionId?: string | null
}

export function trackUnlinkedRefundHardFailure({
  refundInvoice,
  selectedPaymentId,
  refundAmount,
  businessId,
  terminalId,
  transactionId,
  errorMessage,
  refundStartTime,
}: TrackUnlinkedRefundHardFailureParams): void {
  trackEvent(TrackingEvent.ADD_PAYMENT_V2_UNLINKED_REFUND_HARD_FAILURE, {
    timestamp: Date.now(),
    invoiceId: refundInvoice.originalInvoiceId,
    invoiceType: getInvoiceType(refundInvoice),
    businessId,
    customerId: refundInvoice.client?.id || null,
    transactionId: transactionId || null,
    refundAmount,
    paymentId: selectedPaymentId || null,
    errorMessage: errorMessage || null,
    processingDurationMs: calculateProcessingDurationMs(refundStartTime),
    terminalId: terminalId || null,
  })
}

export interface TrackUnlinkedRefundInitiationErrorParams {
  businessId: string
  errorMessage: string
  failureReason: string
  refundAmount: number
  refundInvoice: RefundInvoice
  selectedPaymentId: string
  terminalId?: string | null
}

export function trackUnlinkedRefundInitiationError({
  refundInvoice,
  selectedPaymentId,
  refundAmount,
  businessId,
  terminalId,
  failureReason,
  errorMessage,
}: TrackUnlinkedRefundInitiationErrorParams): void {
  trackEvent(TrackingEvent.ADD_PAYMENT_V2_UNLINKED_REFUND_INITIATION_ERROR, {
    timestamp: Date.now(),
    invoiceId: refundInvoice.originalInvoiceId,
    invoiceType: getInvoiceType(refundInvoice),
    businessId,
    customerId: refundInvoice.client?.id || null,
    refundAmount,
    paymentId: selectedPaymentId,
    failureReason,
    errorMessage,
    terminalId: terminalId || null,
  })
}

export interface TrackLinkedRefundStartedParams {
  businessId: string
  refundAmount: number
  refundInvoice: RefundInvoice
  selectedPayment: Payment | GraphQlPayment
}

export function trackLinkedRefundStarted({
  refundInvoice,
  selectedPayment,
  refundAmount,
  businessId,
}: TrackLinkedRefundStartedParams): void {
  trackEvent(TrackingEvent.ADD_PAYMENT_V2_LINKED_REFUND_STARTED, {
    timestamp: Date.now(),
    invoiceId: refundInvoice.originalInvoiceId,
    invoiceType: getInvoiceType(refundInvoice),
    businessId,
    customerId: refundInvoice.client?.id || null,
    paymentId: selectedPayment.id,
    refundAmount,
  })
}

export interface TrackLinkedRefundProcessingParams {
  businessId: string
  refundAmount: number
  refundInvoice: RefundInvoice
  selectedPaymentId: string
  transactionId: string
}

export function trackLinkedRefundProcessing({
  refundInvoice,
  selectedPaymentId,
  refundAmount,
  businessId,
  transactionId,
}: TrackLinkedRefundProcessingParams): void {
  trackEvent(TrackingEvent.ADD_PAYMENT_V2_LINKED_REFUND_PROCESSING, {
    timestamp: Date.now(),
    invoiceId: refundInvoice.originalInvoiceId,
    invoiceType: getInvoiceType(refundInvoice),
    businessId,
    customerId: refundInvoice.client?.id || null,
    transactionId,
    refundAmount,
    paymentId: selectedPaymentId,
  })
}

export interface TrackLinkedRefundSuccessParams {
  businessId: string
  refundInvoice: RefundInvoice
  refundStartTime: number | null
  selectedPaymentId?: string | null
  transaction: CompletedRefundTransaction
}

export function trackLinkedRefundSuccess({
  refundInvoice,
  selectedPaymentId,
  transaction,
  businessId,
  refundStartTime,
}: TrackLinkedRefundSuccessParams): void {
  trackEvent(TrackingEvent.ADD_PAYMENT_V2_LINKED_REFUND_SUCCESS, {
    timestamp: Date.now(),
    invoiceId: refundInvoice.originalInvoiceId,
    invoiceType: getInvoiceType(refundInvoice),
    businessId,
    customerId: refundInvoice.client?.id || null,
    transactionId: transaction.id,
    refundAmount: transaction.amount,
    paymentId: selectedPaymentId || null,
    processingDurationMs: calculateProcessingDurationMs(refundStartTime),
  })
}

export interface TrackLinkedRefundFailureParams {
  businessId: string
  failureReason: string | null
  refundAmount: number
  refundInvoice: RefundInvoice
  refundStartTime: number | null
  selectedPaymentId?: string | null
  transaction: FailedRefundTransaction
}

export function trackLinkedRefundFailure({
  refundInvoice,
  selectedPaymentId,
  transaction,
  refundAmount,
  businessId,
  failureReason,
  refundStartTime,
}: TrackLinkedRefundFailureParams): void {
  trackEvent(TrackingEvent.ADD_PAYMENT_V2_LINKED_REFUND_FAILURE, {
    timestamp: Date.now(),
    invoiceId: refundInvoice.originalInvoiceId,
    invoiceType: getInvoiceType(refundInvoice),
    businessId,
    customerId: refundInvoice.client?.id || null,
    transactionId: transaction.id,
    refundAmount,
    paymentId: selectedPaymentId || null,
    failureReason,
    processingDurationMs: calculateProcessingDurationMs(refundStartTime),
  })
}

export interface TrackLinkedRefundHardFailureParams {
  businessId: string
  errorMessage?: string | null
  refundAmount: number
  refundInvoice: RefundInvoice
  refundStartTime: number | null
  selectedPaymentId?: string | null
  transactionId?: string | null
}

export function trackLinkedRefundHardFailure({
  refundInvoice,
  selectedPaymentId,
  refundAmount,
  businessId,
  transactionId,
  errorMessage,
  refundStartTime,
}: TrackLinkedRefundHardFailureParams): void {
  trackEvent(TrackingEvent.ADD_PAYMENT_V2_LINKED_REFUND_HARD_FAILURE, {
    timestamp: Date.now(),
    invoiceId: refundInvoice.originalInvoiceId,
    invoiceType: getInvoiceType(refundInvoice),
    businessId,
    customerId: refundInvoice.client?.id || null,
    transactionId: transactionId || null,
    refundAmount,
    paymentId: selectedPaymentId || null,
    errorMessage: errorMessage || null,
    processingDurationMs: calculateProcessingDurationMs(refundStartTime),
  })
}

export interface TrackLinkedRefundInitiationErrorParams {
  businessId: string
  errorMessage: string
  failureReason: string
  refundAmount: number
  refundInvoice: RefundInvoice
  selectedPaymentId: string
  transactionId?: string | null
}

export function trackLinkedRefundInitiationError({
  refundInvoice,
  selectedPaymentId,
  refundAmount,
  businessId,
  transactionId,
  failureReason,
  errorMessage,
}: TrackLinkedRefundInitiationErrorParams): void {
  trackEvent(TrackingEvent.ADD_PAYMENT_V2_LINKED_REFUND_INITIATION_ERROR, {
    timestamp: Date.now(),
    invoiceId: refundInvoice.originalInvoiceId,
    invoiceType: getInvoiceType(refundInvoice),
    businessId,
    customerId: refundInvoice.client?.id || null,
    transactionId: transactionId || null,
    refundAmount,
    paymentId: selectedPaymentId,
    failureReason,
    errorMessage,
  })
}

export interface TrackRefundInvoiceRefetchParams {
  businessId: string
  clientId?: string | null
  originalInvoiceId: string
  refetchReason: string
  refundInvoiceId: string
  transactionId?: string | null
}

export function trackRefundInvoiceRefetch({
  refundInvoiceId,
  originalInvoiceId,
  refetchReason,
  businessId,
  clientId,
  transactionId,
}: TrackRefundInvoiceRefetchParams): void {
  trackEvent(TrackingEvent.ADD_PAYMENT_V2_REFUND_INVOICE_REFETCH, {
    timestamp: Date.now(),
    invoiceId: originalInvoiceId,
    businessId,
    customerId: clientId || null,
    refundInvoiceId,
    originalInvoiceId,
    refetchReason,
    ...(transactionId && { transactionId }),
  })
}
